import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldIcon, ZapIcon, HeartPulseIcon, BrainIcon, CircleIcon, FlaskConicalIcon, ShieldPlusIcon, BatteryChargingIcon, ArrowRightIcon } from 'lucide-react';
const Shiitake = () => {
  const [activeSection, setActiveSection] = useState('metabolic');
  return <div className="w-full bg-white" data-id="element-1343">
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 pt-24 pb-16 bg-gradient-to-b from-purple-50 to-purple-100" data-id="element-1344">
        <div className="max-w-4xl mx-auto" data-id="element-1345">
          <div className="w-20 h-1 bg-purple-500 mx-auto mb-8 rounded-full" data-id="element-1346"></div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4" data-id="element-1347">
            Resilience — Shiitake
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto mb-10" data-id="element-1348">
            Adaptogenic balance with dark richness.
          </p>
        </div>
      </section>

      {/* Benefits Categories Section with Side Navigation */}
      <section className="py-16 px-4 bg-white" data-id="element-1349">
        <div className="container mx-auto" data-id="element-1350">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1351">
            Shiitake Spora: 9 Benefit Categories
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto text-center mb-6" data-id="element-1352">
            Our proprietary extraction technology unlocks the full spectrum of
            Shiitake bioactives, delivering nine distinct benefit categories in
            a single cup.
          </p>
          {/* Spec Summary Bar */}
          <div className="max-w-3xl mx-auto bg-gray-50 rounded-xl p-4 mb-16 text-center" data-id="element-1353">
            <p className="text-sm font-medium tracking-wide uppercase" data-id="element-1354">
              Shiitake Spora | Metabolic Balance + Immunonutrients +
              Antioxidants
            </p>
          </div>

          {/* Desktop: Sticky side navigation */}
          <div className="hidden md:flex" data-id="element-1355">
            <div className="w-64 sticky top-32 self-start pr-8" data-id="element-1356">
              <nav className="flex flex-col space-y-4" data-id="element-1357">
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'metabolic' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('metabolic')} data-id="element-1358">
                  <div className="flex items-center gap-3" data-id="element-1359">
                    <div className="w-5 h-5" data-id="element-1360" />
                    <span data-id="element-1361">Metabolic Balance</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'immunonutrients' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('immunonutrients')} data-id="element-1362">
                  <div className="flex items-center gap-3" data-id="element-1363">
                    <ShieldPlusIcon className="w-5 h-5" data-id="element-1364" />
                    <span data-id="element-1365">Immunonutrients</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'antioxidants' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('antioxidants')} data-id="element-1366">
                  <div className="flex items-center gap-3" data-id="element-1367">
                    <ShieldIcon className="w-5 h-5" data-id="element-1368" />
                    <span data-id="element-1369">Antioxidants</span>
                  </div>
                </button>
              </nav>
            </div>
            <div className="flex-1 max-w-3xl" data-id="element-1370">
              {/* Metabolic Balance Section */}
              <section id="metabolic" className={`pb-24 ${activeSection === 'metabolic' ? 'block' : 'hidden'}`} data-id="element-1371">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1372">
                  The Metabolic Balance of Shiitake
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1373">
                  Shiitake regulates cholesterol and hormone precursors,
                  improves mitochondrial function, and supports vascular health
                  for cardiovascular resilience.
                </p>
                {/* 3-column layout for desktop */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1374">
                  {/* Endocrine Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1375">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1376">
                      <FlaskConicalIcon className="w-6 h-6" data-id="element-1377" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1378">
                      Endocrine Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1379">
                      Metabolic function
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1380">
                      <span className="font-medium" data-id="element-1381">Experience:</span> Stable
                      cardiovascular and metabolic function.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1382">
                      <span className="font-medium" data-id="element-1383">Why it works:</span>{' '}
                      Ergosterols and eritadenine regulate cholesterol and
                      hormone precursors.
                    </p>
                    <div className="mt-auto" data-id="element-1384">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1385">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1386">
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1387">
                          Ergosterols
                        </span>
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1388">
                          Eritadenine
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Bioenergetics */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1389">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1390">
                      <ZapIcon className="w-6 h-6" data-id="element-1391" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1392">Bioenergetics</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1393">
                      Consistent energy
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1394">
                      <span className="font-medium" data-id="element-1395">Experience:</span>{' '}
                      Consistent baseline energy.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1396">
                      <span className="font-medium" data-id="element-1397">Why it works:</span>{' '}
                      Polysaccharides improve mitochondrial function.
                    </p>
                    <div className="mt-auto" data-id="element-1398">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1399">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1400">
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1401">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Circulatory Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1402">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1403">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-1404" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1405">
                      Circulatory Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1406">
                      Cardiovascular resilience
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1407">
                      <span className="font-medium" data-id="element-1408">Experience:</span>{' '}
                      Cardiovascular resilience.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1409">
                      <span className="font-medium" data-id="element-1410">Why it works:</span>{' '}
                      Eritadenine supports vascular health and cholesterol
                      regulation.
                    </p>
                    <div className="mt-auto" data-id="element-1411">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1412">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1413">
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1414">
                          Eritadenine
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Immunonutrients Section */}
              <section id="immunonutrients" className={`pb-24 ${activeSection === 'immunonutrients' ? 'block' : 'hidden'}`} data-id="element-1415">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1416">
                  The Resilience Matrix
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1417">
                  Shiitake contains unique compounds that strengthen immune
                  activity, support balance under stress, and enhance lung
                  immune defenses.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1418">
                  {/* Immunonutrients */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1419">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1420">
                      <ShieldPlusIcon className="w-6 h-6" data-id="element-1421" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1422">Immunonutrients</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1423">
                      Immune readiness
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1424">
                      <span className="font-medium" data-id="element-1425">Experience:</span> Improved
                      readiness to defend against illness.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1426">
                      <span className="font-medium" data-id="element-1427">Why it works:</span>{' '}
                      β-glucans and lentinan strengthen immune activity.
                    </p>
                    <div className="mt-auto" data-id="element-1428">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1429">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1430">
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1431">
                          β-glucans
                        </span>
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1432">
                          Lentinan
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Adaptogens */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1433">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1434">
                      <CircleIcon className="w-6 h-6" data-id="element-1435" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1436">Adaptogens</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1437">
                      Resilient energy
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1438">
                      <span className="font-medium" data-id="element-1439">Experience:</span> Resilient
                      energy and mood.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1440">
                      <span className="font-medium" data-id="element-1441">Why it works:</span>{' '}
                      Ergosterols support balance under stress.
                    </p>
                    <div className="mt-auto" data-id="element-1442">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1443">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1444">
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1445">
                          Ergosterols
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Respiratory Health */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1446">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1447">
                      <div className="w-6 h-6" data-id="element-1448" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1449">
                      Respiratory Health
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1450">
                      Breathing support
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1451">
                      <span className="font-medium" data-id="element-1452">Experience:</span> Daily
                      breathing support.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1453">
                      <span className="font-medium" data-id="element-1454">Why it works:</span>{' '}
                      Polysaccharides enhance lung immune defenses.
                    </p>
                    <div className="mt-auto" data-id="element-1455">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1456">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1457">
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1458">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Antioxidants Section */}
              <section id="antioxidants" className={`pb-24 ${activeSection === 'antioxidants' ? 'block' : 'hidden'}`} data-id="element-1459">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1460">
                  The Protection Network
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1461">
                  Shiitake contains powerful compounds that neutralize free
                  radicals, reduce systemic inflammation, and protect neurons
                  from oxidative damage.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1462">
                  {/* Antioxidants */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1463">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1464">
                      <ShieldIcon className="w-6 h-6" data-id="element-1465" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1466">Antioxidants</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1467">
                      Healthy aging
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1468">
                      <span className="font-medium" data-id="element-1469">Experience:</span> Support
                      healthy aging and recovery.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1470">
                      <span className="font-medium" data-id="element-1471">Why it works:</span>{' '}
                      Lentinan and ergothioneine neutralize free radicals and
                      protect cells.
                    </p>
                    <div className="mt-auto" data-id="element-1472">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1473">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1474">
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1475">
                          Lentinan
                        </span>
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1476">
                          Ergothioneine
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Anti-Inflammatory */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1477">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1478">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-1479" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1480">
                      Anti-Inflammatory
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1481">
                      Reduced strain
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1482">
                      <span className="font-medium" data-id="element-1483">Experience:</span> Less
                      strain on body systems over time.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1484">
                      <span className="font-medium" data-id="element-1485">Why it works:</span>{' '}
                      Lentinan and sterols reduce systemic inflammation.
                    </p>
                    <div className="mt-auto" data-id="element-1486">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1487">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1488">
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1489">
                          Lentinan
                        </span>
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1490">
                          Sterols
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Neuroprotective Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1491">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1492">
                      <BrainIcon className="w-6 h-6" data-id="element-1493" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1494">Neuroprotective</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1495">
                      Cognitive clarity
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1496">
                      <span className="font-medium" data-id="element-1497">Experience:</span> Maintain
                      long-term cognitive clarity.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1498">
                      <span className="font-medium" data-id="element-1499">Why it works:</span>{' '}
                      Lentinan and ergothioneine protect neurons from oxidative
                      damage.
                    </p>
                    <div className="mt-auto" data-id="element-1500">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1501">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1502">
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1503">
                          Lentinan
                        </span>
                        <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium" data-id="element-1504">
                          Ergothioneine
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* Mobile: Collapsible sections */}
          <div className="md:hidden space-y-8" data-id="element-1505">
            {/* Spec Summary Bar - Mobile */}
            <div className="bg-gray-50 rounded-xl p-4 mb-8 text-center" data-id="element-1506">
              <p className="text-xs font-medium tracking-wide uppercase" data-id="element-1507">
                Shiitake Spora | Metabolic Balance + Immunonutrients +
                Antioxidants
              </p>
            </div>
            <div data-id="element-1508">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'metabolic' ? '' : 'metabolic')} data-id="element-1509">
                <div className="flex items-center gap-3" data-id="element-1510">
                  <div className="w-5 h-5" data-id="element-1511" />
                  <h2 className="text-xl font-bold" data-id="element-1512">Metabolic Balance</h2>
                </div>
                <span data-id="element-1513">{activeSection === 'metabolic' ? '−' : '+'}</span>
              </button>
              {activeSection === 'metabolic' && <div className="p-4 space-y-8" data-id="element-1514">
                  {/* Mobile content for Metabolic Balance section */}
                </div>}
            </div>
            <div data-id="element-1515">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'immunonutrients' ? '' : 'immunonutrients')} data-id="element-1516">
                <div className="flex items-center gap-3" data-id="element-1517">
                  <ShieldPlusIcon className="w-5 h-5" data-id="element-1518" />
                  <h2 className="text-xl font-bold" data-id="element-1519">The Resilience Matrix</h2>
                </div>
                <span data-id="element-1520">{activeSection === 'immunonutrients' ? '−' : '+'}</span>
              </button>
              {activeSection === 'immunonutrients' && <div className="p-4 space-y-8" data-id="element-1521">
                  {/* Mobile content for Immunonutrients section */}
                </div>}
            </div>
            <div data-id="element-1522">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'antioxidants' ? '' : 'antioxidants')} data-id="element-1523">
                <div className="flex items-center gap-3" data-id="element-1524">
                  <ShieldIcon className="w-5 h-5" data-id="element-1525" />
                  <h2 className="text-xl font-bold" data-id="element-1526">The Protection Network</h2>
                </div>
                <span data-id="element-1527">{activeSection === 'antioxidants' ? '−' : '+'}</span>
              </button>
              {activeSection === 'antioxidants' && <div className="p-4 space-y-8" data-id="element-1528">
                  {/* Mobile content for Antioxidants section */}
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-16 px-4 bg-black text-white text-center" data-id="element-1529">
        <div className="max-w-4xl mx-auto" data-id="element-1530">
          <h2 className="text-3xl font-bold mb-6" data-id="element-1531">Experience Shiitake Spora</h2>
          <p className="text-xl max-w-2xl mx-auto mb-8" data-id="element-1532">
            Adaptogenic balance with dark richness that builds resilience for
            everyday challenges.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1533">
            <Link to="/#products" className="bg-purple-500 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-purple-600 transition-colors" data-id="element-1534">
              Get Spora
            </Link>
            <Link to="/how-it-works" className="bg-transparent text-white border border-white px-8 py-3 rounded-full text-sm font-medium hover:bg-white/10 transition-colors" data-id="element-1535">
              Learn The Ritual
            </Link>
            <Link to="/" className="bg-white text-black px-8 py-3 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center" data-id="element-1536">
              <span data-id="element-1537">Explore All Varieties</span>
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1538" />
            </Link>
          </div>
        </div>
      </section>
    </div>;
};
export default Shiitake;